/**
 * Captured pieces are derived from the board FEN — the server stopped sending
 * them (the wire arrays are always []). Ported verbatim from PlayChess
 * `getCapturedPieces` (src/Screens/PlayChess/index.js L226-254): pure,
 * module-scope, guards empty/invalid input -> {w: [], b: []}.
 */
export type CapturedPieces = {w: string[]; b: string[]};

export const EMPTY_CAPTURED: CapturedPieces = {w: [], b: []};

/**
 * Piece values, mirrored from PlayChess `CapturedPieces/index.js` L11-26
 * (`getPieceValue`). The old file gives a knight 2.99 so that a knight sorts
 * BEFORE a bishop; the tray's order is `PIECE_ORDER` here and the lead is
 * rounded, so a flat 3 produces the same order and the same number.
 *
 * Kings are never captured; the key keeps the table total.
 */
export const PIECE_VALUES: {[type: string]: number} = {
  p: 1,
  n: 3,
  b: 3,
  r: 5,
  q: 9,
  k: 0,
};

/** the order a tray stacks the groups in: cheapest first (old L76-85) */
export const PIECE_ORDER = ['p', 'n', 'b', 'r', 'q'] as const;

/** the material a list of captured codes is worth; case-insensitive */
export function materialValue(pieces: string[]): number {
  return pieces.reduce(
    (total, piece) => total + (PIECE_VALUES[piece.toLowerCase()] || 0),
    0,
  );
}

/**
 * How much material each side is AHEAD by, 0 when level or behind — the old
 * screen labels the leader only (`pieceValueDifference > 0`, L138).
 *
 * `captured.w` are the WHITE pieces missing from the board, i.e. what BLACK has
 * taken; `captured.b` is what WHITE has taken.
 */
export function materialLead(captured: CapturedPieces): {
  white: number;
  black: number;
} {
  const diff = Math.round(
    materialValue(captured.b) - materialValue(captured.w),
  );
  return {white: Math.max(0, diff), black: Math.max(0, -diff)};
}

export function getCapturedPieces(fen: string | null): CapturedPieces {
  if (!fen || typeof fen !== 'string') {
    return {w: [], b: []};
  }
  const initialPieceCount: any = {
    w: {p: 8, n: 2, b: 2, r: 2, q: 1, k: 1},
    b: {p: 8, n: 2, b: 2, r: 2, q: 1, k: 1},
  };
  const currentPieceCount: any = {w: {}, b: {}};
  const [board] = fen.split(' ');
  board.split('/').forEach(row => {
    for (const char of row) {
      if (Number.isInteger(parseInt(char, 10))) {
        continue; // empty squares
      }
      const color = char === char.toUpperCase() ? 'w' : 'b';
      const type = char.toLowerCase();
      currentPieceCount[color][type] =
        (currentPieceCount[color][type] || 0) + 1;
    }
  });
  const capturedPieces: CapturedPieces = {w: [], b: []};
  (['w', 'b'] as const).forEach(color => {
    Object.keys(initialPieceCount[color]).forEach(type => {
      const diff =
        initialPieceCount[color][type] - (currentPieceCount[color][type] || 0);
      for (let i = 0; i < diff; i++) {
        capturedPieces[color].push(color === 'w' ? type.toUpperCase() : type);
      }
    });
  });
  return capturedPieces;
}
